import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  settingsButtonClass,
  settingsSelectTriggerClass,
} from "@/components/shared/settings/SettingsKit";
import { getHttpStatus } from "@/lib/observability/shared";
import {
  logAutoTopUpEvent,
  reportBillingFailure,
  useBillingScope,
} from "@/lib/observability/billing";
import type { AutoTopUpStatus } from "@/lib/types/billing";
import { cn } from "@/lib/utils";

const CAP_OPTIONS = [1, 2, 3, 5, 10, 20];

const compact = new Intl.NumberFormat("en", { notation: "compact" });

const ERROR_COPY: Record<string, string> = {
  no_payment_method: "No card on file",
  not_api_plan: "Auto top-up is only on the Agents plan",
  topup_unavailable: "Top-ups aren't switched on yet",
};

const errorCode = (error: unknown) =>
  (error as { code?: string } | null)?.code ?? null;

type Props = {
  autoTopUp: AutoTopUpStatus;
  save: (settings: { enabled: boolean; maxPerPeriod?: number }) => Promise<AutoTopUpStatus>;
  saving: boolean;
};

/** Rows for the Agents plan "Auto top-up" card on Account settings. */
export default function AutoTopUpRows({ autoTopUp, save, saving }: Props) {
  const scope = useBillingScope();
  const capOptions = CAP_OPTIONS.includes(autoTopUp.maxPerPeriod)
    ? CAP_OPTIONS
    : [...CAP_OPTIONS, autoTopUp.maxPerPeriod].sort((a, b) => a - b);

  const submit = async (enabled: boolean, maxPerPeriod = autoTopUp.maxPerPeriod) => {
    try {
      await save({ enabled, maxPerPeriod });
      logAutoTopUpEvent("auto_top_up_updated", scope, { enabled, maxPerPeriod });
      toast.success(enabled ? "Auto top-up on" : "Auto top-up off");
    } catch (error) {
      const code = errorCode(error);
      if (getHttpStatus(error) !== null && (getHttpStatus(error) as number) < 500) {
        logAutoTopUpEvent("auto_top_up_rejected", scope, { enabled, maxPerPeriod, errorCode: code });
      }
      reportBillingFailure("update_auto_top_up", error, scope);
      toast.error(code && ERROR_COPY[code] ? ERROR_COPY[code] : "Couldn't save auto top-up", {
        description: code && ERROR_COPY[code] ? undefined : "Try again in a moment.",
      });
    }
  };

  const failedTitle =
    autoTopUp.failureCode === "requires_action" || autoTopUp.failureCode === "authentication_required"
      ? "Last auto top-up needs your bank's approval"
      : "Last auto top-up was declined";

  return (
    <>
      <div className="flex items-center justify-between gap-4 px-5 py-[18px]">
        <div className="flex min-w-0 flex-col gap-[3px]">
          <label htmlFor="auto-top-up" className="text-xs font-medium leading-4">
            Auto top-up
          </label>
          <span className="text-xs leading-4 text-foreground/60 tabular-nums">
            {compact.format(autoTopUp.blockPoints * autoTopUp.blocks)} data points at{" "}
            {Math.round(autoTopUp.threshold * 100)}% used
          </span>
        </div>
        <Switch
          id="auto-top-up"
          checked={autoTopUp.enabled}
          disabled={saving}
          onCheckedChange={(checked) => void submit(checked)}
        />
      </div>

      {autoTopUp.enabled && (
        <div className="flex flex-col gap-3 border-t border-border px-5 py-[18px] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-col gap-[3px]">
            <label htmlFor="auto-top-up-cap" className="text-xs font-medium leading-4">
              Monthly limit
            </label>
            <span className="text-xs leading-4 text-foreground/60 tabular-nums">
              {autoTopUp.usedThisPeriod} of {autoTopUp.maxPerPeriod} used this month
            </span>
          </div>
          <Select
            value={String(autoTopUp.maxPerPeriod)}
            disabled={saving}
            onValueChange={(value) => void submit(true, Number(value))}
          >
            <SelectTrigger id="auto-top-up-cap" className={cn(settingsSelectTriggerClass, "sm:w-[180px]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {capOptions.map((cap) => (
                <SelectItem key={cap} value={String(cap)}>
                  {cap} top-up{cap === 1 ? "" : "s"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {autoTopUp.paymentFailed && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-warning-tint px-5 py-3.5">
          <span className="flex items-center gap-2 text-xs font-medium leading-4 text-warning">
            <span className="size-1.5 shrink-0 rounded-full bg-warning" />
            {failedTitle}
          </span>
          <Button
            variant="outline"
            disabled={saving}
            onClick={() => void submit(true)}
            className={cn(settingsButtonClass, "border-input bg-background")}
          >
            Retry
          </Button>
        </div>
      )}
    </>
  );
}
